import axios, { AxiosInstance } from 'axios';
import { getHubSpotContext } from '@/lib/hubspotContext';
import { hubSpotUserHeaders } from '@/lib/hubspotUser';
import { getHubSpotSessionToken, retryWithRenewedSession } from '@/lib/hubspotSession';
import type {
  BillingPeriod,
  BulkAction,
  BulkOutcome,
  BulkPublishImpact,
  CatalogEvent,
  HubSpotUserOption,
  ProductComputation,
  ProductDetail,
  ProductListItem,
  ProductReviewer,
  ProductSnapshot,
  ProductSpec,
  ProductStatusCounts,
  ProductStatusFilter,
  ProductUsage,
  PublishImpact,
  QuoteRowStatus,
  RateImpact,
  RateView,
  ReviewerGroup,
  SpecValidationIssue,
  ProductWorkbench,
  WorkbenchChanges,
} from '@/types/product';

const BACKEND_URL = import.meta.env.DEV ? '/api' : 'https://ubo-huws.onrender.com/api';

const createClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_URL || BACKEND_URL,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((config) => {
    const token = getHubSpotSessionToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    for (const [key, value] of Object.entries(hubSpotUserHeaders())) {
      config.headers[key] = value;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error) => retryWithRenewedSession(error, client)
  );
  return client;
};

const client = createClient();

const portalId = () => getHubSpotContext().portalId;
const rateCatalogReady = new Map<string, Promise<void>>();
const ensureRateCatalog = async (requestedPortalId?: string) => {
  const portal = requestedPortalId ?? portalId();
  const key = portal ?? '__global__';
  if (!rateCatalogReady.has(key)) {
    const ready = client
      .post('/products/rates/sync-catalog', { portalId: portal })
      .then(() => undefined)
      .catch((error) => {
        rateCatalogReady.delete(key);
        throw error;
      });
    rateCatalogReady.set(key, ready);
  }
  await rateCatalogReady.get(key);
};

export interface ProductFilters {
  category?: string;
  labels?: string[];
  search?: string;
  includeDisabled?: boolean;
  includeArchived?: boolean;
  onlyNeedsReview?: boolean;
  /** Neemt de archief- en uitgeschakeld-scoping over wanneer meegegeven. */
  status?: ProductStatusFilter;
}

export const productApi = {
  async previewRateRemoval(rateId: string, replacementId?: string): Promise<{ products: Array<{ id: string; name: string; variants: Array<{ name: string; fromCents: number | null; toCents: number | null }>; errors: string[] }> }> {
    const { data } = await client.post('/products/rates/preview-removal', { portalId: portalId(), rateId, replacementId });
    return data;
  },
  async removeRate(rateId: string, replacementId: string): Promise<{ productsUpdated: number }> {
    const { data } = await client.post('/products/rates/remove', { portalId: portalId(), rateId, replacementId });
    return data;
  },
  async workbench(): Promise<ProductWorkbench> {
    await ensureRateCatalog();
    const { data } = await client.get('/products/workbench', { params: { portalId: portalId() } });
    return data;
  },

  async applyWorkbench(
    changes: WorkbenchChanges
  ): Promise<{ productsUpdated: number; ratesUpdated: number }> {
    const { data } = await client.post('/products/workbench/apply', {
      ...changes,
      portalId: portalId(),
    });
    return data;
  },

  async list(filters: ProductFilters = {}): Promise<ProductListItem[]> {
    const { data } = await client.get('/products', {
      params: {
        portalId: portalId(),
        category: filters.category,
        labels: filters.labels?.length ? filters.labels.join(',') : undefined,
        search: filters.search || undefined,
        includeDisabled: filters.includeDisabled ? 'true' : undefined,
        includeArchived: filters.includeArchived ? 'true' : undefined,
        onlyNeedsReview: filters.onlyNeedsReview ? 'true' : undefined,
        status: filters.status,
      },
    });
    return data;
  },

  async taxonomy(): Promise<{
    categories: string[];
    labels: string[];
    counts: ProductStatusCounts;
  }> {
    const { data } = await client.get('/products/taxonomy', { params: { portalId: portalId() } });
    return data;
  },

  async hubspotUsers(): Promise<HubSpotUserOption[]> {
    const currentPortalId = portalId();
    if (!currentPortalId) throw new Error('HubSpot-portaal ontbreekt');
    const { data } = await client.get(
      `/hubspot/portal/${encodeURIComponent(currentPortalId)}/users`
    );
    return data;
  },

  async get(id: string): Promise<ProductDetail> {
    await ensureRateCatalog();
    const { data } = await client.get(`/products/${id}`, {
      params: { portalId: portalId() },
    });
    return data;
  },

  async create(input: {
    name: string;
    category?: string;
    labels?: string[];
    kind?: 'service' | 'bundle';
    billingPeriod?: string;
    spec?: Partial<ProductSpec>;
  }): Promise<{ id: string }> {
    const { data } = await client.post('/products', { ...input, portalId: portalId() });
    return data;
  },

  /** Kopie als nieuw concept; spec en facturatieperiode zoals ze in het werkblad staan. */
  async duplicate(
    id: string,
    input: { name: string; spec?: ProductSpec; billingPeriod?: BillingPeriod }
  ): Promise<{ id: string; name: string }> {
    const { data } = await client.post(`/products/${id}/duplicate`, {
      ...input,
      portalId: portalId(),
    });
    return data;
  },

  async saveDraft(
    id: string,
    input: Record<string, unknown> & { spec: ProductSpec }
  ): Promise<{ issues: SpecValidationIssue[]; computation: ProductComputation }> {
    const { data } = await client.put(`/products/${id}/draft`, input);
    return data;
  },

  async discardDraft(id: string): Promise<void> {
    await client.post(`/products/${id}/draft/discard`);
  },

  /** Rekent een concept door zonder op te slaan; voor de live preview in de editor. */
  async preview(
    spec: ProductSpec
  ): Promise<{ computation: ProductComputation; issues: SpecValidationIssue[] }> {
    const { data } = await client.post('/products/preview', { spec, portalId: portalId() });
    return data;
  },

  async publishImpact(id: string): Promise<PublishImpact> {
    const { data } = await client.get(`/products/${id}/publish-impact`);
    return data;
  },

  async publish(id: string, reason: string): Promise<unknown> {
    const { data } = await client.post(`/products/${id}/publish`, { reason });
    return data;
  },

  // -------------------------------------------------------------------- bulk

  async bulkPublishImpact(ids: string[]): Promise<BulkPublishImpact> {
    const { data } = await client.post('/products/bulk/publish-impact', { ids });
    return data;
  },

  async bulk(input: {
    ids: string[];
    action: BulkAction;
    reason?: string;
    label?: string;
  }): Promise<BulkOutcome> {
    const { data } = await client.post('/products/bulk', input);
    return data;
  },

  async setReviewers(
    ids: string[],
    selection: { reviewerIds: string[]; groupIds: string[] }
  ): Promise<{
    updated: Array<{ id: string; name: string }>;
    reviewers: ProductReviewer[];
    groups: Array<{ id: string; name: string }>;
  }> {
    const { data } = await client.post('/products/bulk', {
      ids,
      action: 'set-reviewers',
      reviewerIds: selection.reviewerIds,
      groupIds: selection.groupIds,
      portalId: portalId(),
    });
    return data;
  },

  // ------------------------------------------------------- keuzegroepen

  async reviewerGroups(): Promise<ReviewerGroup[]> {
    const { data } = await client.get('/products/reviewer-groups', {
      params: { portalId: portalId() },
    });
    return data;
  },

  async createReviewerGroup(input: {
    name: string;
    memberOwnerIds: string[];
  }): Promise<ReviewerGroup> {
    const { data } = await client.post('/products/reviewer-groups', {
      ...input,
      portalId: portalId(),
    });
    return data;
  },

  async updateReviewerGroup(
    groupId: string,
    input: { name?: string; memberOwnerIds?: string[] }
  ): Promise<ReviewerGroup> {
    const { data } = await client.patch(
      `/products/reviewer-groups/${encodeURIComponent(groupId)}`,
      { ...input, portalId: portalId() }
    );
    return data;
  },

  async deleteReviewerGroup(groupId: string): Promise<void> {
    await client.delete(`/products/reviewer-groups/${encodeURIComponent(groupId)}`, {
      params: { portalId: portalId() },
    });
  },

  async setEnabled(id: string, enabled: boolean): Promise<void> {
    await client.post(`/products/${id}/enable`, { enabled });
  },

  async setArchived(id: string, archived: boolean): Promise<void> {
    await client.post(`/products/${id}/archive`, { archived });
  },

  async history(id: string): Promise<CatalogEvent[]> {
    const { data } = await client.get(`/products/${id}/history`);
    return data;
  },

  async catalogHistory(limit = 100): Promise<CatalogEvent[]> {
    const { data } = await client.get('/products/history', {
      params: { portalId: portalId(), limit },
    });
    return data;
  },

  async usage(id: string): Promise<ProductUsage[]> {
    const { data } = await client.get(`/products/${id}/usage`);
    return data;
  },

  async snapshot(id: string, variantId?: string): Promise<ProductSnapshot> {
    const { data } = await client.get(`/products/${id}/snapshot`, { params: { variantId } });
    return data;
  },

  // ---------------------------------------------------------------- tarieven

  async rates(requestedPortalId?: string): Promise<RateView[]> {
    await ensureRateCatalog(requestedPortalId);
    const { data } = await client.get('/products/rates', { params: { portalId: requestedPortalId ?? portalId() } });
    return data;
  },

  async createRate(input: {
    key: string;
    name: string;
    group?: string;
    amountCents: number;
    validFrom?: string;
  }): Promise<RateView> {
    const { data } = await client.post('/products/rates', { ...input, portalId: portalId() });
    return data;
  },

  /**
   * Laat vóór het doorvoeren zien welke producten en welke lopende offertes een
   * tariefwijziging raakt. Zonder rollen in de catalogus is dit de enige rem.
   */
  async previewRateChange(input: {
    rateKey: string;
    amountCents: number;
    validFrom: string;
  }): Promise<RateImpact> {
    const { data } = await client.post('/products/rates/preview-change', {
      ...input,
      portalId: portalId(),
    });
    return data;
  },

  async applyRateChange(input: {
    rateKey: string;
    amountCents: number;
    validFrom: string;
    reason: string;
  }): Promise<{ impact: RateImpact }> {
    const { data } = await client.post('/products/rates/apply-change', {
      ...input,
      portalId: portalId(),
    });
    return data;
  },

  async deleteRateEntry(entryId: string): Promise<void> {
    await client.delete(`/products/rates/entries/${entryId}`);
  },

  // ------------------------------------------------------------ offerteregels

  async quoteStatus(quoteId: string): Promise<QuoteRowStatus[]> {
    const { data } = await client.get(`/products/quotes/${quoteId}/status`);
    return data;
  },

  async syncUsages(quoteId: string, blockId: string, rows: unknown[]): Promise<QuoteRowStatus[]> {
    const { data } = await client.post(`/products/quotes/${quoteId}/blocks/${blockId}/usages`, {
      rows,
    });
    return data;
  },

  async syncQuoteUsages(
    quoteId: string,
    blocks: Array<{ blockId: string; rows: unknown[] }>
  ): Promise<QuoteRowStatus[]> {
    const { data } = await client.put(`/products/quotes/${quoteId}/usages`, { blocks });
    return data;
  },
};

export default productApi;
